import { has } from '@/commonFunctions/utils'
import { MatchByPlayType } from '@/components/match/MatchByPlayType'
import { ConfirmModal } from '@/components/modal'
import { EVENT_MODIFY } from '@/constants/Common'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { useAppSelector } from '@/hooks/useRedux'
import { clubSelector } from '@/pages/club/selectors'
import { userSelector } from '@/pages/user/selectors'
import {
  useDirectorAddScores,
  useDirectorConfirmBracketMatch,
  useDirectorEditScores,
  useEditConfirmMatch,
  useForfeitBracketMatch,
  usePlayerAddScores,
  usePlayerEditScores,
  usePostSubstitutePlayers,
} from '@/services'
import { BracketMatch } from '@/types'
import { isEmpty, sortBy } from 'lodash'
import moment from 'moment'
import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { eventSelector } from '../selectors'
import { AddEditScoresModal } from './AddEditScoresModal'
import { MatchMenu } from './BracketMatchMenu'
import { ForfeitModal } from './ForfeitModal'
import { PlayerButton } from './PlayerButton'
import { ScoreTeam } from './ScoreTeam'

type Props = {
  match: BracketMatch
  bracketId?: string
  callbackFetch?: () => void
}

export const BracketMatchItem = (props: Props) => {
  return (
    <MatchByPlayType
      matchPlayType={props.match.matchPlayType}
      matchDate={props.match.matchDate}
      adjustmentData={{ eventName: props.match.leagueName, match: props.match }}
      regular={() => <BracketMatchItemRegular {...props} />}
    />
  )
}

const BracketMatchItemRegular = ({ match, bracketId, callbackFetch }: Props) => {
  const { t } = useTranslation()

  const { permissions } = useAppSelector(clubSelector)
  const { bracket } = useAppSelector(eventSelector)
  const { user } = useAppSelector(userSelector)

  const { mutate: postSubstitutePlayers } = usePostSubstitutePlayers()
  const { mutate: editConfirmMatch } = useEditConfirmMatch()
  const { mutate: directorEditScores } = useDirectorEditScores()
  const { mutate: directorAddScores } = useDirectorAddScores()
  const { mutate: playerEditScores } = usePlayerEditScores()
  const { mutate: playerAddScores } = usePlayerAddScores()
  const { mutate: forfeitBracketMatch } = useForfeitBracketMatch()
  const { mutate: confirmBracketMatch } = useDirectorConfirmBracketMatch()

  const isClubDirector = useMemo(() => has(permissions, EVENT_MODIFY), [permissions])

  const [subPlayers, setSubPlayers] = useState<any>()
  const [playerIdsOfMatch, setPlayerIdsOfMatch] = useState<any>()
  const [disableSave, setDisableSave] = useState(true)
  const [excludeData, setExcludeData] = useState<any>([])
  const [showForfeitModal, setShowForfeitModal] = useState(false)
  const [forfeitTeam, setForfeitTeam] = useState<string | null>(null)
  const [showEditScoresModal, setShowEditScoresModal] = useState(false)
  const [showAddScoresModal, setShowAddScoresModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [isValidateModalOpened, setIsValidateModalOpened] = useState<boolean>(false)

  const sortedTeams = sortBy(match?.teams, 'serial')

  let matchCreatedTeam: any[] = []
  const teamA = [sortedTeams?.[0]?.player1?.id, sortedTeams[0]?.player2?.id]
  const teamB = [sortedTeams?.[1]?.player1?.id, sortedTeams[1]?.player2?.id]
  if (teamA.includes(match?.userId)) {
    matchCreatedTeam = teamA
  } else if (teamB.includes(match?.userId)) {
    matchCreatedTeam = teamB
  }

  const toggleForfeitModal = () => setShowForfeitModal(!showForfeitModal)

  const toggleEditScoresModal = () => setShowEditScoresModal(!showEditScoresModal)

  const toggleAddScoresModal = () => {
    setShowAddScoresModal(!showAddScoresModal)
  }

  const toggleDeleteModal = () => setDeleteModal(!deleteModal)

  const addSubPlayers = () => {
    const subCommonDetails = {
      bracketId,
      leagueMatchId: match.leagueMatchId,
      isClubMember: false,
      isPlayerSubstituted: false,
      subPlayerId: 0,
    }
    setSubPlayers([
      {
        ...subCommonDetails,
        playerId: sortedTeams?.[0]?.player1?.id,
        allowSubstitution: sortedTeams?.[0]?.player1?.allowSubstitution,
      },
      sortedTeams?.[0]?.player2?.id
        ? {
            ...subCommonDetails,
            playerId: sortedTeams?.[0]?.player2?.id,
            allowSubstitution: sortedTeams?.[0]?.player2?.allowSubstitution,
          }
        : {},
      {
        ...subCommonDetails,
        playerId: sortedTeams?.[1]?.player1?.id,
        allowSubstitution: sortedTeams?.[1]?.player1?.allowSubstitution,
      },
      sortedTeams?.[1]?.player2?.id
        ? {
            ...subCommonDetails,
            playerId: sortedTeams?.[1]?.player2?.id,
            allowSubstitution: sortedTeams?.[1]?.player2?.allowSubstitution,
          }
        : {},
    ])
    setPlayerIdsOfMatch([
      sortedTeams?.[0]?.player1?.id,
      sortedTeams?.[0]?.player2?.id,
      sortedTeams?.[1]?.player1?.id,
      sortedTeams?.[1]?.player2?.id,
    ])
  }

  const handleAddScores = (data: any) => {
    const payload = {
      bracketId: match?.bracketId,
      matchDate: moment(data?.matchDate?.$d || data?.matchDate).format('YYYY-MM-DD'),
      leagueMatchId: match?.leagueMatchId,
      matchId: match?.matchId,
      format: bracket?.format,
      noOfGames: data?.games?.length,
      team1: {
        game1: data?.games?.[0]?.team1,
        game2: data?.games[1]?.team1 >= 0 ? data?.games[1]?.team1 : -1,
        game3: data?.games[2]?.team1 >= 0 ? data?.games[2]?.team1 : -1,
        game4: data?.games[3]?.team1 >= 0 ? data?.games[3]?.team1 : -1,
        game5: data?.games[4]?.team1 >= 0 ? data?.games[4]?.team1 : -1,
        player1: match?.teams?.[0]?.player1?.id,
        player2: match?.teams?.[0]?.player2?.id,
        winner: data?.winner === 'team1',
      },
      team2: {
        game1: data?.games[0]?.team2,
        game2: data?.games[1]?.team2 >= 0 ? data?.games[1]?.team2 : -1,
        game3: data?.games[2]?.team2 >= 0 ? data?.games[2]?.team2 : -1,
        game4: data?.games[3]?.team2 >= 0 ? data?.games[3]?.team2 : -1,
        game5: data?.games[4]?.team2 >= 0 ? data?.games[4]?.team2 : -1,
        player1: match?.teams?.[1]?.player1?.id,
        player2: match?.teams?.[1]?.player2?.id,
        winner: data?.winner === 'team2',
      },
    }
    isClubDirector
      ? directorAddScores(payload, {
          onSuccess: () => {
            toast.success(t('common.success'))
            if (callbackFetch) callbackFetch()
          },
          onError: (error: any) => {
            toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
          },
        })
      : playerAddScores(payload, {
          onSuccess: () => {
            toast.success(t('common.success'))
            if (callbackFetch) callbackFetch()
          },
          onError: (error: any) => {
            toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
          },
        })
  }

  const handleEditScores = (data: any, flag: any, flagForfeit = false) => {
    const commonData = {
      leagueMatchId: match?.leagueMatchId,
      matchDate: moment(data?.matchDate?.$d || data?.matchDate).format('YYYY-MM-DD'),
      matchId: match?.matchId,
      team1Score: {
        game1: data?.games?.[0]?.team1 || 0,
        game2: data?.games?.[1]?.team1 >= 0 ? data?.games?.[1]?.team1 : -1,
        game3: data?.games?.[2]?.team1 >= 0 ? data?.games?.[2]?.team1 : -1,
        game4: data?.games?.[3]?.team1 >= 0 ? data?.games?.[3]?.team1 : -1,
        game5: data?.games?.[4]?.team1 >= 0 ? data?.games?.[4]?.team1 : -1,
        player1: match?.teams[0]?.player1?.id,
        player2: match?.teams[0]?.player2?.id,
        winner: data?.winner === 'team1' || forfeitTeam === 'Team B',
        teamId: match?.teams?.[0]?.id,
      },
      team2Score: {
        game1: data?.games?.[0]?.team2 || 0,
        game2: data?.games?.[1]?.team2 >= 0 ? data?.games?.[1]?.team2 : -1,
        game3: data?.games?.[2]?.team2 >= 0 ? data?.games?.[2]?.team2 : -1,
        game4: data?.games?.[3]?.team2 >= 0 ? data?.games?.[3]?.team2 : -1,
        game5: data?.games?.[4]?.team2 >= 0 ? data?.games?.[4]?.team2 : -1,
        player1: match?.teams[1]?.player1?.id,
        player2: match?.teams[1]?.player2?.id,
        winner: data?.winner === 'team2' || forfeitTeam === 'Team A',
        teamId: match?.teams?.[1]?.id,
      },
    }
    if (flag) {
      const payload = {
        ...commonData,
        bracketId: match?.bracketId,
        isForfeited: flagForfeit,
      }
      editConfirmMatch(payload, {
        onSuccess: () => {
          toast.success(t('common.success'))
          if (callbackFetch) callbackFetch()
        },
        onError: (error: any) => {
          toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
        },
      })
    } else {
      const payload = {
        noOfGames: data?.games?.length,
        ...commonData,
      }
      isClubDirector
        ? directorEditScores(payload, {
            onSuccess: () => {
              toast.success(t('common.success'))
              if (callbackFetch) callbackFetch()
            },
            onError: (error: any) => {
              toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
            },
          })
        : playerEditScores(payload, {
            onSuccess: () => {
              toast.success(t('common.success'))
              if (callbackFetch) callbackFetch()
            },
            onError: (error: any) => {
              toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
            },
          })
    }
  }

  const handleValidateMatch = () => {
    confirmBracketMatch(
      { matchId: match.matchId, leagueMatchId: match.leagueMatchId || 0, userId: user?.id || 0 },
      {
        onSuccess: () => {
          toast.success(t('common.success'))
          if (callbackFetch) callbackFetch()
          setIsValidateModalOpened(false)
        },
        onError: (error: any) => {
          toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
        },
      }
    )
  }

  const forfeitMatch = () => {
    const payload = {
      bracketId,
      clubId: bracket?.clubId,
      leagueMatchId: match?.leagueMatchId,
      matchId: match?.matchId,
      team1: {
        isForfeited: forfeitTeam === 'Team A',
        player1: match?.teams?.[0]?.player1?.id,
        player2: match?.teams?.[0]?.player2?.id,
        teamId: match?.teams?.[0]?.id,
      },
      team2: {
        isForfeited: forfeitTeam === 'Team B',
        player1: match?.teams?.[1]?.player1?.id,
        player2: match?.teams?.[1]?.player2?.id,
        teamId: match?.teams?.[1]?.id,
      },
    }
    forfeitBracketMatch(payload, {
      onSuccess: () => {
        toast.success(t('common.success'))
        if (callbackFetch) callbackFetch()
      },
      onError: (error: any) => {
        toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
      },
    })
  }

  const clearSubPlayers = () => {
    setSubPlayers([])
    setPlayerIdsOfMatch([])
  }

  const handleAddPlayer = (selectedPlayer: any, id: any) => {
    if (selectedPlayer?.id) {
      const arr = subPlayers.map((item: any) => {
        return item.playerId === id
          ? {
              ...item,
              subPlayerId: selectedPlayer?.id,
              subPlayerFullName: selectedPlayer?.fullName,
              subPlayerImageUrl: selectedPlayer?.imageUrl,
            }
          : item
      })
      setSubPlayers(arr)
      setDisableSave(false)
      setExcludeData([...excludeData, selectedPlayer?.id])
      setPlayerIdsOfMatch([...playerIdsOfMatch, selectedPlayer.id])
    }
  }

  const handleSaveChanges = () => {
    const subArray = subPlayers.filter((item: any) => item?.isPlayerSubstituted === true)
    const payload = subArray.map((item: any) => ({
      bracketId: item.bracketId,
      leagueMatchId: item.leagueMatchId,
      isClubMember: item.isClubMember,
      playerId: item.playerId,
      subPlayerId: item.subPlayerId,
    }))
    if (!isEmpty(payload)) {
      postSubstitutePlayers(payload, {
        onSuccess: () => {
          toast.success(t('common.success'))
          if (callbackFetch) callbackFetch()
        },
        onError: (error: any) => {
          toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
        },
      })
      clearSubPlayers()
    }
  }

  const removePlayer = (id: any) => {
    const arr = subPlayers.map((item: any) => {
      return item.playerId === id ? { ...item, isPlayerSubstituted: true, subPlayerId: 0 } : item
    })
    const arr1 = subPlayers.filter((item: any) => item?.playerId === id)
    if (excludeData.includes(arr1?.[0]?.subPlayerId)) {
      excludeData.splice(excludeData.indexOf(arr1?.[0]?.subPlayerId), 1)
    }
    const filterPlayerIds = [...playerIdsOfMatch].filter((item) => item !== id)
    setSubPlayers(arr)
    setDisableSave(true)
    setPlayerIdsOfMatch(filterPlayerIds)
  }

  const isPlayerInMatch = () =>
    [
      sortedTeams[0]?.player1?.id,
      sortedTeams[0]?.player2?.id,
      sortedTeams[1]?.player1?.id,
      sortedTeams[1]?.player2?.id,
    ].includes(user?.id as number)

  return (
    <>
      <div className="bg-white rounded-2xl p-4 flex flex-col gap-y-2 relative">
        <div className="flex justify-between items-center">
          <span className="text-xs font-semibold text-navy-900">
            {!match?.matchDate ? 'TBD' : moment(match?.matchDate).format('MMMM D, YYYY')}
          </span>
          <div className="flex items-center gap-x-2">
            {match?.displayIdentity && (
              <span className="text-xs font-semibold text-navy-900 text-opacity-50">
                {t('common.matchId')} {match?.displayIdentity}
              </span>
            )}
            {match?.isForfeited ? (
              <p
                className={` bg-warn text-warn bg-opacity-20 rounded-lg px-2 h-6 flex items-center font-medium text-xs`}
              >
                {t('event.forfeit')}
              </p>
            ) : (
              (isClubDirector ||
                (isPlayerInMatch() && !match?.confirmed && user && !matchCreatedTeam.includes(user.id))) &&
              !isEmpty(match?.teams[1]) && (
                <MatchMenu
                  match={match}
                  sortedTeams={sortedTeams}
                  addSubPlayers={addSubPlayers}
                  onForfeit={toggleForfeitModal}
                  onEditScores={toggleEditScoresModal}
                  onAddScores={toggleAddScoresModal}
                  onDeleteMatch={toggleDeleteModal}
                  onValidateMatch={() => setIsValidateModalOpened(true)}
                  isPlayerInMatch={isPlayerInMatch()}
                />
              )
            )}

            {isEmpty(match?.teams[1]) && (
              <p
                className={` bg-slate-400 text-navy-900 text-opacity-30 bg-opacity-20 rounded-lg px-2 h-6 flex items-center font-medium text-xs`}
              >
                {t('event.bye')}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-row flex-1 gap-x-6">
          <div className="flex-1">
            <div className="flex justify-between flex-1">
              <div className="flex flex-1 flex-col w-6/12">
                <PlayerButton
                  player={sortedTeams?.[0]?.player1}
                  isWinner={sortedTeams?.[0]?.winner}
                  subPlayers={subPlayers}
                  removePlayer={removePlayer}
                  excludeData={excludeData}
                  handleAddPlayer={(player) => handleAddPlayer(player, sortedTeams?.[0]?.player1?.id)}
                />
                {sortedTeams?.[0]?.player2 && (
                  <PlayerButton
                    player={sortedTeams?.[0]?.player2}
                    isWinner={sortedTeams?.[0]?.winner}
                    subPlayers={subPlayers}
                    removePlayer={removePlayer}
                    excludeData={excludeData}
                    handleAddPlayer={(player) => handleAddPlayer(player, sortedTeams?.[0]?.player2?.id)}
                  />
                )}
              </div>
              <ScoreTeam team={sortedTeams?.[0]} opponentTeam={sortedTeams?.[1]} />
            </div>

            {!isEmpty(match?.teams[1]) && (
              <>
                <div className="h-[0.5px] bg-black bg-opacity-10 md:my-2 my-1" />

                <div className="flex justify-between flex-1">
                  <div className="flex flex-1 flex-col w-6/12">
                    <PlayerButton
                      player={sortedTeams?.[1]?.player1}
                      isWinner={sortedTeams?.[1]?.winner}
                      subPlayers={subPlayers}
                      removePlayer={removePlayer}
                      excludeData={excludeData}
                      handleAddPlayer={(player) => handleAddPlayer(player, sortedTeams?.[1]?.player1?.id)}
                    />

                    {sortedTeams?.[1]?.player2 && (
                      <PlayerButton
                        player={sortedTeams?.[1]?.player2}
                        isWinner={sortedTeams?.[1]?.winner}
                        subPlayers={subPlayers}
                        removePlayer={removePlayer}
                        excludeData={excludeData}
                        handleAddPlayer={(player) => handleAddPlayer(player, sortedTeams?.[1]?.player2?.id)}
                      />
                    )}
                  </div>
                  <ScoreTeam team={sortedTeams?.[1]} opponentTeam={sortedTeams?.[0]} />
                </div>
              </>
            )}
          </div>
          {!isEmpty(subPlayers) && (
            <div className="flex flex-col gap-y-4 items-center justify-center">
              <button
                className="text-xs text-navy-900 font-semibold border shadow-md px-2 py-1 rounded w-full hover:bg-slate-100"
                onClick={clearSubPlayers}
              >
                {t('common.cancel')}
              </button>
              <button
                className={`text-xs font-semibold px-2 py-1 rounded w-full ${
                  disableSave ? 'bg-slate-300 text-gray-500' : 'bg-primary-500 hover:bg-opacity-70 text-white'
                }`}
                onClick={handleSaveChanges}
                disabled={disableSave}
              >
                {t('common.saveChanges')}
              </button>
            </div>
          )}
        </div>
      </div>
      <ForfeitModal
        open={showForfeitModal}
        onClose={() => {
          toggleForfeitModal()
          setForfeitTeam(null)
        }}
        disabled={!forfeitTeam}
        match={match}
        onSubmit={() => {
          if (match?.leagueMatchStatus === 'CONFIRMED') {
            handleEditScores(match, true, true)
          } else {
            forfeitMatch()
          }
          setForfeitTeam(null)
        }}
        forfeitTeam={forfeitTeam}
        setForfeitTeam={setForfeitTeam}
      />
      <AddEditScoresModal
        open={showEditScoresModal}
        onClose={toggleEditScoresModal}
        match={match}
        title={t('event.editScores')}
        handleOnSubmit={handleEditScores}
        director={isClubDirector}
      />
      <AddEditScoresModal
        open={showAddScoresModal}
        onClose={toggleAddScoresModal}
        match={match}
        title={t('event.addScores')}
        handleOnSubmit={handleAddScores}
        director={isClubDirector}
      />
      <ConfirmModal
        open={!!deleteModal}
        onClose={toggleDeleteModal}
        onSubmit={toggleDeleteModal}
        title={t('common.deleteMatch')}
        desc={t('event.deleteBracketMatchDesc')}
        submitTitle={t('common.delete')}
      />
      <ConfirmModal
        open={isValidateModalOpened}
        onSubmit={() => handleValidateMatch()}
        onClose={() => setIsValidateModalOpened(false)}
        title={t('event.validateMatch')}
        desc={t('event.validateMatchDesc')}
      />
    </>
  )
}
